import {DataObjectId} from 'yougile-model'
import {TreeListItem} from '@components/common/base/tree-list/tree-list'

export const findAndFilterListBySearch = (
  list: TreeListItem[],
  search: string,
  selected: DataObjectId[]
): TreeListItem[] => {
  if (!search) {
    return list
  }

  return list.reduce<TreeListItem[]>((acc, listItem) => {
    const titleHasSearchString = listItem.title.toLowerCase().includes(search)

    if (selected.includes(listItem.id) || titleHasSearchString) {
      acc.push(listItem)
      return acc
    }

    if (listItem.subItems?.length) {
      const subItems = findAndFilterListBySearch(
        listItem.subItems,
        search,
        selected
      )

      if (subItems.length) {
        acc.push({
          ...listItem,
          subItems
        })
      }
      return acc
    }

    return acc
  }, [])
}

export const findNode = (
  nodeId: DataObjectId,
  list: TreeListItem[]
): TreeListItem | undefined => {
  for (const node of list) {
    if (node.id === nodeId) {
      return node
    }

    if (node.subItems?.length) {
      const item = findNode(nodeId, node.subItems)
      if (item) {
        return item
      }
    }
  }

  return undefined
}

export const findAllNodesIdsInBranch = (
  branch: TreeListItem
): DataObjectId[] => {
  if (!branch.subItems?.length) {
    return [branch.id]
  }

  return branch.subItems?.reduce<DataObjectId[]>((acc, item) => {
    if (!item.subItems?.length) {
      acc.push(item.id)
      return acc
    }
    return [...acc, ...findAllNodesIdsInBranch(item)]
  }, [])
}

export const findAllCollapsableNodes = (
  list: TreeListItem[]
): DataObjectId[] => {
  return list.reduce<DataObjectId[]>((acc, node) => {
    if (node.subItems?.length) {
      acc.push(node.id)

      const subItemsNodes = findAllCollapsableNodes(node.subItems)

      return [...acc, ...subItemsNodes]
    }

    return acc
  }, [])
}

export const getIsItemSelected = (
  selectedNodes: DataObjectId[],
  node: TreeListItem
): boolean | undefined => {
  if (!selectedNodes.length) {
    return false
  }

  if (!node.subItems?.length) {
    return selectedNodes.includes(node.id)
  }

  const selectedSubItemsCount = node.subItems.reduce<number>((acc, subItem) => {
    const isSelected = getIsItemSelected(selectedNodes, subItem)
    if (isSelected === undefined || isSelected) {
      return acc + 1
    }
    return acc
  }, 0)

  if (selectedSubItemsCount === 0) {
    return false
  }
  if (selectedSubItemsCount === node.subItems.length) {
    return true
  }
  return undefined
}

interface CountedElements {
  [level: number]: number
}
export const countSelectedItemsByLevel = (
  selected: DataObjectId[],
  nodes: TreeListItem[]
): CountedElements => {
  const countByLevel: CountedElements = {}

  const traverse = (node: TreeListItem, level: number): void => {
    if (countByLevel[level] === undefined) {
      countByLevel[level] = 0
    }

    if (selected.includes(node.id)) {
      countByLevel[level]++
    }

    if (node.subItems) {
      const prevLevelValue = countByLevel[level + 1] || 0
      node.subItems.forEach(subItem => traverse(subItem, level + 1))
      const currentLevelValue = countByLevel[level + 1]
      if (currentLevelValue > prevLevelValue) {
        countByLevel[level]++
      }
    }
  }

  nodes.forEach(node => traverse(node, 0))

  return countByLevel
}
